import React, { useEffect, useRef, useCallback } from 'react'
import { Button, Col, Container, Row, TopBar } from 'components'
import './profile.scss'
import { FarmerIcon } from 'assets'
import { colors } from 'styles/colors'
import { useBindDispatch } from 'hooks'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store/configStore'
import { constants, getConnectUser, internetConnect, setOpenedVillageSession } from 'helpers'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { EnrollmentRecap } from 'components/enrollmentRecap/EnrollmentRecap'
import { fetchEnrollmentRecapAction } from 'features/enrollmentRecap/enrollmentRecapSlice'

const DEFAULT_ENROLLMENT_RECAP = {
    loading: true,
    error: '',
    hasLoadedOnce: false,
    sourceCounts: {}
}

// Each poll is two Core Services-backed requests (village orders + today's
// interactions), so keep the default conservative. Ticks are skipped while
// the tab is hidden and a refresh is triggered when it becomes visible again.
export const ENROLLMENT_RECAP_POLL_INTERVAL_MS =
    Number(process.env.REACT_APP_ENROLLMENT_RECAP_POLL_INTERVAL_MS) ||
    2 * 60 * 1000

export const VillageProfile = () => {
    const { t } = useTranslation()
    const { veCode = '', veVillageId = '' } = useParams()

    const { getVeHandler, villageClientListHandler } = useBindDispatch()

    const { ve } = useSelector(({ ve }: RootState) => ve)
    const dispatch = useDispatch<AppDispatch>()
    const enrollmentRecap = useSelector(
        ({ enrollmentRecap }: RootState) =>
            enrollmentRecap?.[veVillageId] ?? DEFAULT_ENROLLMENT_RECAP
    )

    const navigate = useNavigate()
    const veData = getConnectUser()

    useEffect(() => {
        if (veCode) {
            getVeHandler(veCode)
        }
        localStorage.removeItem(constants.CLIENT_DETAIL_LOCAL_STORE)
    }, [])

    // Guards against overlapping requests if a poll ever takes longer than
    // the interval (slow network, large payload) -- without this, the next
    // tick would fire another fetch on top of one still in flight, and an
    // older, slower response could resolve after a newer one and clobber
    // fresher data with stale data. Keyed by village so a fetch still in
    // flight for the previous village never blocks the new village's
    // initial fetch when the route param changes.
    const inFlightRecapVillageRef = useRef<string | null>(null)

    const fetchRecapIfIdle = useCallback(() => {
        const villageId = veVillageId
        if (inFlightRecapVillageRef.current === villageId) {
            return
        }
        inFlightRecapVillageRef.current = villageId
        dispatch(fetchEnrollmentRecapAction({ villageId })).finally(() => {
            if (inFlightRecapVillageRef.current === villageId) {
                inFlightRecapVillageRef.current = null
            }
        })
    }, [dispatch, veVillageId])

    useEffect(() => {
        if (!veVillageId) {
            return
        }

        fetchRecapIfIdle()

        const pollIfVisible = () => {
            if (!document.hidden && internetConnect()) {
                fetchRecapIfIdle()
            }
        }

        const intervalId = setInterval(
            pollIfVisible,
            ENROLLMENT_RECAP_POLL_INTERVAL_MS
        )
        document.addEventListener('visibilitychange', pollIfVisible)

        return () => {
            clearInterval(intervalId)
            document.removeEventListener('visibilitychange', pollIfVisible)
        }
    }, [veVillageId, fetchRecapIfIdle])

    useEffect(() => {
        villageClientListHandler({ villageId: veVillageId })
    }, [])

    const storedVillage = veData.veVillages?.find(
        ({ sf_id: sfId }: { sf_id: string }) => sfId === veVillageId
    )
    const veVillage = ve?.ve_villages?.find(
        ({ sf_id: sfId }: { sf_id: string }) => sfId === veVillageId
    )
    const villageName = storedVillage?.name || veVillage?.name
    const villageCode = storedVillage?.code || veVillage?.code || ''

    const handleVillageRedirection = () => {
        setOpenedVillageSession(
            {
                sf_id: veVillageId,
                name: villageName || '',
                code: villageCode
            },
            {
                zoneName: ve?.zone_name || veData.zoneName,
                zoneSFID: ve?.zone || veData.zoneSFID,
                zone_code:
                    ve?.zone_code ||
                    (veData as { zone_code?: string }).zone_code
            }
        )
        navigate(`/village/${veVillageId}`)
    }

    const handleBackNavigation = () => {
        navigate('/fc/village')
    }

    const handleEnrollmentRecapClick = () => {
        const recapPath = veCode
            ? `/fc/village/${veVillageId}/enrollment-recap/${veCode}`
            : `/fc/village/${veVillageId}/enrollment-recap`
        navigate(`${recapPath}?villageCode=${villageCode}`)
    }

    return (
        <div className="page-wrapper">
            <Container>
                <Row>
                    <Col md={12}>
                        <div className="sticky-header" id="sticky-header">
                            <Container>
                                <TopBar
                                    back
                                    backNavigation={handleBackNavigation}
                                >
                                    {villageName}
                                </TopBar>
                            </Container>
                        </div>
                        <div>
                            <Button
                                style="primary"
                                className="ve-profile--button"
                                ariaLabel="ve-profile-farmers-button"
                                onClick={handleVillageRedirection}
                            >
                                <FarmerIcon color={colors.white} />
                                <span className="ve-profile--button-text">
                                    {t('fcProfile.farmers')}
                                </span>
                            </Button>
                        </div>
                        <div
                            onClick={handleEnrollmentRecapClick}
                            aria-label="enrollment-recap-card"
                        >
                            <EnrollmentRecap
                                loading={enrollmentRecap.loading}
                                error={enrollmentRecap.error}
                                sourceCounts={enrollmentRecap.sourceCounts}
                            />
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}
